<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import DrawerMenuContent from "./DrawerMenuContent.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import BlockBadgeIcon from "@/components/canvas/BlockBadgeIcon.vue";
import {
  useActivityFeed,
  type ActivityRow,
} from "@/composable/useActivityFeed";
import { EntityType } from "@/drawing/entities/detection";

const props = defineProps<{ drawingId: number }>();
const emit = defineEmits<{ (e: "close"): void }>();

const { t } = useTranslation();
const c = useActivityFeed(props.drawingId);

/**
 * Icône par nature CAD réelle (EntityType), plus fine que les 4 catégories de
 * `ActivityKind`. Deux trous connus dans le set Phosphor : pas d'icône
 * ellipse/oval (partage `circle` avec Circle) ni hatch/pattern (`paint-bucket`
 * en proxy — convention "zone remplie").
 */
function kindIcon(e: ActivityRow): string {
  switch (e.entityType) {
    case EntityType.Text:
    case EntityType.MText:
      return "text-t";
    case EntityType.MLeader:
      return "arrow-bend-up-left";
    case EntityType.BlockReference:
      return e.kind === "photoMarker" ? "camera" : "cube";
    case EntityType.Polyline:
      return "path";
    case EntityType.Line:
      return "line-segment";
    case EntityType.Circle:
    case EntityType.Ellipse:
      return "circle";
    case EntityType.Arc:
      return "circle-dashed";
    case EntityType.Hatch:
      return "paint-bucket";
    default:
      return "question";
  }
}

/**
 * Zoome sur l'élément de la ligne, puis demande la fermeture : ouvert en
 * modale (menu fichier), le contenu cache le plan sur lequel on vient de
 * zoomer.
 */
function locateRow(e: ActivityRow) {
  if (!e.handle) return;
  c.locate(e.handle);
  emit("close");
}

function rowName(e: ActivityRow): string {
  if (e.kind === "text") return t("canvas:activity.kind.text");
  if (e.kind === "leader") return t("canvas:activity.kind.leader");
  if (e.kind === "photoMarker") return t("canvas:activity.kind.photoMarker");
  // A defect name says more than the nature of the entity, so it still wins.
  if (e.defectName) return e.defectName;
  if (e.kind === "block" || e.kind === "dynamicBlock") {
    return t("canvas:activity.kind.block");
  }
  return t("canvas:activity.kind.geometry");
}

/** `true` si la valeur est un nombre pur (mode "numéro" plutôt que "date/heure"). */
function isNumericLabel(value: string): boolean {
  return /^\d+$/.test(value);
}

/**
 * Détail "avant → après" pour une ligne "modified" avec un changeKind reconnu.
 * `null` si pas de diff pertinent à afficher (ex. changeKind "moved"/"scale").
 */
function modifiedDetail(e: ActivityRow): string | null {
  if (e.eventKind !== "modified" || e.oldValue == null || e.newValue == null)
    return null;
  if (e.changeKind === "text") return `« ${e.oldValue} » → « ${e.newValue} »`;
  if (e.changeKind === "height") return `${e.oldValue} → ${e.newValue}`;
  if (e.changeKind === "photoNumber") {
    // Le marqueur peut afficher un numéro ou une date/heure selon la légende —
    // le préfixe "n°" n'a de sens que pour un numéro.
    if (isNumericLabel(e.oldValue) && isNumericLabel(e.newValue)) {
      return `n°${e.oldValue} → n°${e.newValue}`;
    }
    return `${e.oldValue} → ${e.newValue}`;
  }
  return null;
}

function rowDetail(e: ActivityRow): string {
  const modDetail = modifiedDetail(e);
  if (modDetail) return modDetail;
  if (e.kind === "text" || e.kind === "leader")
    return e.text ? `« ${e.text} »` : "";
  if (e.kind === "photoMarker") {
    if (!e.markerLabel) return "";
    return isNumericLabel(e.markerLabel)
      ? `n° ${e.markerLabel}`
      : e.markerLabel;
  }
  return e.defectName ? "" : e.layer;
}

function eventBadge(e: ActivityRow): string {
  if (e.eventKind === "created") return t("canvas:activity.created");
  if (e.eventKind === "deleted") return t("canvas:activity.deleted");
  return e.mergedCount && e.mergedCount > 1
    ? t("canvas:activity.modifiedCount", { count: e.mergedCount })
    : t("canvas:activity.modified");
}

function timeLabel(ms?: number): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
</script>

<template>
  <DrawerMenuContent
    :label="$t('canvas:activity.title')"
    :description="$t('canvas:activity.description')"
  >
    <div v-if="c.loading.value" class="text-grey px-1">
      <small>{{ $t("canvas:activity.loading") }}</small>
    </div>

    <div
      v-for="e in c.elements.value"
      :key="`${e.entityUid}-${e.eventKind}-${e.time}`"
      class="act-row"
      :class="{ 'act-row-deleted': e.eventKind === 'deleted' }"
      @click="locateRow(e)"
    >
      <span
        class="bar"
        :style="{ background: e.color || 'rgba(255,255,255,0.25)' }"
      />
      <!-- Dynamic blocks carry the same cube-and-lightning badge as their panel
           button, so a row and the tool that edits it are recognisably the same. -->
      <BlockBadgeIcon
        v-if="e.kind === 'dynamicBlock'"
        class="kind-ic"
        badge="lightning"
        badge-color="#ffc107"
        :size="16"
        :badge-size="10"
      />
      <PhosphorIcon v-else :name="kindIcon(e)" :size="16" class="kind-ic" />
      <div class="col">
        <div class="name text-truncate">
          {{ rowName(e) }}
          <small v-if="rowDetail(e)" class="text-grey">{{
            rowDetail(e)
          }}</small>
          <span class="badge" :class="`badge-${e.eventKind}`">{{
            eventBadge(e)
          }}</span>
        </div>
        <div class="time">
          <PhosphorIcon name="clock" :size="11" /> {{ timeLabel(e.time) }}
        </div>
      </div>
      <PhosphorIcon
        v-if="e.handle"
        name="magnifying-glass-plus"
        :size="16"
        class="locate-ic text-grey"
      />
    </div>

    <div
      v-if="!c.loading.value && !c.elements.value.length"
      class="text-grey px-1"
    >
      <small>{{ $t("canvas:activity.empty") }}</small>
    </div>
  </DrawerMenuContent>
</template>

<style lang="scss" scoped>
.act-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  cursor: pointer;
  padding: 6px 6px;
  border: 1px solid transparent;
  border-radius: 0.4rem;
  margin-bottom: 4px;
  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
}
.act-row-deleted {
  cursor: default;
  opacity: 0.55;
  .name {
    text-decoration: line-through;
  }
  &:hover {
    background-color: transparent;
  }
}
.badge {
  display: inline-block;
  font-size: 0.62rem;
  line-height: 1;
  padding: 2px 5px;
  border-radius: 3px;
  margin-left: 4px;
  vertical-align: middle;
  text-decoration: none;
}
.badge-deleted {
  background-color: rgba(255, 82, 82, 0.2);
  color: #ff8a8a;
}
.badge-modified {
  background-color: rgba(255, 193, 7, 0.2);
  color: #ffc947;
}
.badge-created {
  background-color: rgba(76, 175, 80, 0.2);
  color: #69db7c;
}
.bar {
  width: 4px;
  align-self: stretch;
  min-height: 30px;
  border-radius: 2px;
  flex: none;
}
.kind-ic {
  opacity: 0.8;
  flex: none;
}
.col {
  flex: 1;
  min-width: 0;
}
.name {
  font-size: 0.85rem;
}
.time {
  font-size: 0.7rem;
  opacity: 0.55;
  display: flex;
  align-items: center;
  gap: 3px;
}
.locate-ic {
  opacity: 0.7;
  flex: none;
}
</style>
